import { useEffect, useState } from 'react'
import { SignInCard, OTPCard, RSVPCard, PaymentCard } from '@shoots/ui'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { stripePromise } from '../lib/stripe'
import { deriveView, type OTPStatus } from './rsvp-flow-derive'
import { formatPrice } from '../utils/formatPrice'
import type { PaymentBreakdown } from '../hooks/usePaymentIntent'

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function PaymentCardWithStripe({
  amount,
  breakdown,
  error,
  loading,
  onMethodChange,
  onPaymentResult,
  onPaymentLoading,
}: {
  amount: string
  breakdown: PaymentBreakdown | null
  error?: string
  loading: boolean
  onMethodChange: (method: 'card' | 'us_bank_account') => Promise<void>
  onPaymentResult: (
    result: { status: 'succeeded' | 'processing' } | { error: string }
  ) => void
  onPaymentLoading: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [selectedMethod, setSelectedMethod] = useState<'card' | 'us_bank_account'>('card')

  const handlePay = async () => {
    if (!stripe || !elements) return

    onPaymentLoading()

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })

    if (stripeError) {
      onPaymentResult({ error: stripeError.message ?? 'Payment failed' })
    } else if (paymentIntent?.status === 'succeeded') {
      onPaymentResult({ status: 'succeeded' })
    } else if (paymentIntent?.status === 'processing') {
      onPaymentResult({ status: 'processing' })
    } else {
      onPaymentResult({
        error: `Unexpected payment status: ${paymentIntent?.status ?? 'unknown'}`,
      })
    }
  }

  // When the invitee switches payment method in the Element, re-price the
  // PaymentIntent server-side for that method, then pull the updated amount
  // into the Element via fetchUpdates().
  const handleElementChange = async (event: {
    value: { type: string }
  }) => {
    const type = event.value.type
    if (type !== 'card' && type !== 'us_bank_account') return
    // selectedMethod tracks the last method we requested a reprice for, only to
    // dedupe repeated onChange events — it is NOT used for display.
    if (type === selectedMethod) return
    setSelectedMethod(type)
    await onMethodChange(type)
    if (elements) await elements.fetchUpdates()
  }

  // Drive the ACH notice off the server-confirmed breakdown.method — the same
  // source as the summary numbers — so the notice and the figures can't desync.
  // (The local selection flips synchronously, before the reprice resolves, so
  // using it would briefly show the ACH notice next to the old card totals, and
  // leave it stuck on if the reprice fails.)
  const isAch = breakdown?.method === 'us_bank_account'

  const summary = (
    <div className="payment-summary">
      {breakdown && (
        <dl className="payment-summary-list">
          <div className="payment-summary-row">
            <dt>Cost</dt>
            <dd data-testid="summary-base">{formatCents(breakdown.basePriceCents)}</dd>
          </div>
          <div className="payment-summary-row">
            <dt>Service fee</dt>
            <dd data-testid="summary-fee">{formatCents(breakdown.serviceFeeCents)}</dd>
          </div>
          <div className="payment-summary-row payment-summary-total">
            <dt>Total</dt>
            <dd data-testid="summary-total">{formatCents(breakdown.chargedCents)}</dd>
          </div>
        </dl>
      )}
      <PaymentElement onChange={handleElementChange} />
      {isAch && (
        <p className="payment-ach-notice" role="note">
          Bank payments take a few business days to clear. Your RSVP confirms
          once the payment settles.
        </p>
      )}
    </div>
  )

  return (
    <PaymentCard
      amount={amount}
      onPay={handlePay}
      error={error}
      loading={loading}
      showFeeRow={false}
      stripeSlot={summary}
    />
  )
}

export interface RSVPFlowProps {
  authenticated: boolean
  rsvpStatus: 'unanswered' | 'confirmed' | 'cant-go'
  rsvpTitle: string
  rsvpSubtitle?: string
  rsvpStatusLabel?: string
  initialPhone?: string
  authError?: string | null
  onSendOTP: (phone: string) => Promise<void>
  onVerifyOTP: (phone: string, code: string) => Promise<void>
  onResendOTP: (phone: string) => Promise<void>
  onStatusChange: (status: 'confirmed' | 'cant-go') => Promise<void>
  /**
   * Called when Stripe SDK reports the payment succeeded. Use this to
   * refetch gathering so server state (my_payment_status='paid') catches
   * up to the optimistic confirmation derive already routed to.
   */
  onPaymentSuccess?: () => void
  responding?: boolean
  responseError?: string | null
  eventPrice?: number | null
  myPaymentStatus?: 'paid' | 'processing' | 'pending' | null
  paymentClientSecret: string | null
  paymentError: string | null
  breakdown: PaymentBreakdown | null
  onMethodChange: (method: 'card' | 'us_bank_account') => Promise<void>
}

export function RSVPFlow({
  authenticated,
  rsvpStatus,
  rsvpTitle,
  rsvpSubtitle,
  rsvpStatusLabel,
  initialPhone,
  authError,
  onSendOTP,
  onVerifyOTP,
  onResendOTP,
  onStatusChange,
  onPaymentSuccess,
  responding,
  responseError,
  eventPrice,
  myPaymentStatus,
  paymentClientSecret,
  paymentError,
  breakdown,
  onMethodChange,
}: RSVPFlowProps) {
  const [signInPhone, setSignInPhone] = useState(initialPhone ?? '')
  const [signInError, setSignInError] = useState<string | undefined>()
  const [signInLoading, setSignInLoading] = useState(false)

  const [otpActive, setOtpActive] = useState(false)
  const [otpPhone, setOtpPhone] = useState('')
  const [otpValue, setOtpValue] = useState('')
  const [otpStatus, setOtpStatus] = useState<OTPStatus>('default')

  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [paymentSubmitError, setPaymentSubmitError] = useState<string | undefined>()
  const [paymentLocalStatus, setPaymentLocalStatus] = useState<
    'succeeded' | 'processing' | null
  >(null)

  const [shouldShowAuthError, setShouldShowAuthError] = useState(false)

  // When the user becomes authenticated, drop the OTP wizard. The derive
  // function handles the auth → rsvp / payment / confirmed routing.
  useEffect(() => {
    if (authenticated) {
      setOtpActive(false)
      setOtpValue('')
      setOtpStatus('default')
    }
  }, [authenticated])

  const view = deriveView({
    authenticated,
    rsvpStatus,
    myPaymentStatus,
    eventPrice,
    paymentClientSecret,
    paymentError,
    signIn: { phone: signInPhone, error: signInError, loading: signInLoading },
    otp: {
      active: otpActive,
      phone: otpPhone,
      value: otpValue,
      status: otpStatus,
    },
    paymentSubmitting,
    paymentSubmitError,
    paymentLocalStatus,
  })

  // Stop pinning a stale auth error once the user clears the sign-in error.
  useEffect(() => {
    if (view.kind === 'sign-in' && signInError === undefined) {
      setShouldShowAuthError(false)
    }
  }, [view.kind, signInError])

  const handleSendOTP = async () => {
    const digits = signInPhone.replace(/\D/g, '')
    if (digits.length < 10) {
      setSignInError('Please enter a valid phone number')
      return
    }
    setSignInLoading(true)
    setSignInError(undefined)
    setShouldShowAuthError(true)
    try {
      const phoneWithCode = digits.length === 10 ? `+1${digits}` : `+${digits}`
      await onSendOTP(phoneWithCode)
      setOtpPhone(phoneWithCode)
      setOtpValue('')
      setOtpStatus('default')
      setOtpActive(true)
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : 'Failed to send code')
    } finally {
      setSignInLoading(false)
    }
  }

  const handleVerifyOTP = async (code: string) => {
    setOtpStatus('verifying')
    setShouldShowAuthError(true)
    try {
      await onVerifyOTP(otpPhone, code)
    } catch {
      setOtpStatus('error')
      setOtpValue('')
    }
  }

  const handleResendOTP = async () => {
    setShouldShowAuthError(true)
    try {
      await onResendOTP(otpPhone)
    } catch {
      setOtpStatus('error')
      setOtpValue('')
    }
  }

  const handleBackToSignIn = () => {
    setSignInPhone(otpPhone.replace(/^\+1/, ''))
    setSignInError(undefined)
    setSignInLoading(false)
    setOtpActive(false)
  }

  const handleStatusChange = async (status: 'confirmed' | 'cant-go') => {
    try {
      await onStatusChange(status)
    } catch (err) {
      console.error('RSVP failed:', err)
    }
  }

  switch (view.kind) {
    case 'rsvp':
      return (
        <RSVPCard
          status={rsvpStatus}
          title={rsvpTitle}
          subtitle={rsvpSubtitle}
          statusLabel={rsvpStatusLabel}
          error={responseError}
          disabled={responding}
          onStatusChange={handleStatusChange}
        />
      )

    case 'payment-loading':
      return (
        <PaymentCard
          amount={eventPrice ? formatPrice(eventPrice) : ''}
          onPay={() => {}}
          error={view.error}
          loading={true}
          showFeeRow={false}
          stripeSlot={
            <div
              role="status"
              aria-label="Loading payment form"
              style={{
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-action-primary)]" />
            </div>
          }
        />
      )

    case 'payment':
      return (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: view.clientSecret }}
        >
          <PaymentCardWithStripe
            amount={
              breakdown ? formatCents(breakdown.chargedCents) : formatPrice(eventPrice!)
            }
            breakdown={breakdown}
            error={view.error}
            loading={view.loading}
            onMethodChange={onMethodChange}
            onPaymentLoading={() => {
              setPaymentSubmitting(true)
              setPaymentSubmitError(undefined)
            }}
            onPaymentResult={result => {
              setPaymentSubmitting(false)
              if ('error' in result) {
                setPaymentSubmitError(result.error)
                return
              }
              // Mirror Stripe's local result into paymentLocalStatus so
              // derive routes to confirmed/processing on the next render —
              // the server (webhook → event_payments → gathering refetch)
              // lags by seconds, and we don't want the card stuck open.
              setPaymentSubmitError(undefined)
              setPaymentLocalStatus(result.status)
              if (result.status === 'succeeded') onPaymentSuccess?.()
            }}
          />
        </Elements>
      )

    case 'processing':
      return (
        <RSVPCard
          status="confirmed"
          title="You're going!"
          subtitle="Payment submitted — we'll confirm once it clears"
          statusLabel="Payment processing"
          disabled={true}
          onStatusChange={onStatusChange}
        />
      )

    case 'confirmed':
      // Paid-confirmed is irreversible without a refund (Gap 6, not built),
      // so disable the toggle. Free-confirmed has nothing to undo — let
      // the invitee flip back to "Can't go" if they change their mind.
      return (
        <RSVPCard
          status="confirmed"
          title="You're going!"
          subtitle={rsvpSubtitle}
          statusLabel="RSVP confirmed"
          disabled={view.reason === 'paid'}
          onStatusChange={handleStatusChange}
        />
      )

    case 'otp':
      return (
        <OTPCard
          phone={view.phone}
          value={view.value}
          onChange={setOtpValue}
          onComplete={handleVerifyOTP}
          onResend={handleResendOTP}
          onBack={handleBackToSignIn}
          status={view.status}
        />
      )

    case 'sign-in':
      return (
        <SignInCard
          phone={view.phone}
          onPhoneChange={v => {
            setSignInPhone(v)
            setSignInError(undefined)
          }}
          onSubmit={handleSendOTP}
          error={
            view.error ||
            (shouldShowAuthError ? (authError ?? undefined) : undefined)
          }
          loading={view.loading}
        />
      )
  }
}
